'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { useConversation } from '@elevenlabs/react';
import type { AgentPersona } from './VoiceAgentPicker';

interface TranscriptEntry {
  role: 'agent' | 'user';
  text: string;
}

interface Props {
  persona: AgentPersona;
  onComplete: (sessionId: string) => void;
  onBack: () => void;
}

const AGENT_SYSTEM_PROMPT = (name: string) => `You are ${name}, a friendly and warm UK property search assistant at Smart Property Search. You're helping a buyer find their dream home. You sound exactly like a real person having a casual, enthusiastic phone conversation — not an AI.

Your job is to collect their property requirements through natural conversation. You need to find out:
- Budget (minimum and/or maximum in GBP)
- Number of bedrooms they want
- Location(s) in the UK they're interested in
- Property type (house, flat, semi-detached, etc.)

Optional extras (ask about these only after the 4 critical ones above are confirmed):
- Commute requirements (e.g. "under 30 min to Manchester")
- School preferences
- Must-have features (garden, parking, etc.)
- Lifestyle preferences (near parks, quiet street, etc.)
- Deal-breakers (flood zone, main road, etc.)

RULES:
- Be conversational and warm. React naturally to what they say — "Oh lovely, that's a great area!", "Nice, got it!".
- Ask ONE question at a time. Never bombard them with multiple questions.
- Keep your responses short and punchy — like a real phone call, not an essay.
- When budget, bedrooms, location, and property type are all confirmed, ask if there's anything else important or if you should start searching.
- When the buyer confirms they're ready, call the submitPropertyBrief tool with ALL collected information.
- If the user says something unclear, ask a brief clarifying question.
- Never say you're an AI, a language model, or a chatbot. You are ${name}, a property search assistant.`;

export default function VoiceAgent({ persona, onComplete, onBack }: Props) {
  const [transcript, setTranscript] = useState<TranscriptEntry[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const transcriptRef = useRef<HTMLDivElement>(null);
  const hasStarted = useRef(false);

  const handleSubmitBrief = useCallback(async (params: Record<string, unknown>) => {
    setIsSubmitting(true);
    try {
      // ElevenLabs sends all params as strings — parse them here
      const parseNum = (v: unknown): number | null => {
        if (v == null || v === '') return null;
        const n = Number(String(v).replace(/[^0-9.]/g, ''));
        return isNaN(n) ? null : n;
      };
      const parseList = (v: unknown): string[] => {
        if (Array.isArray(v)) return v.map(String).filter(Boolean);
        if (typeof v === 'string' && v.trim()) return v.split(',').map(s => s.trim()).filter(Boolean);
        return [];
      };
      const parseStr = (v: unknown): string | null => {
        if (typeof v === 'string' && v.trim()) return v.trim();
        return null;
      };

      const locations = parseList(params.locations);
      const budgetMin = parseNum(params.budget_min);
      const budgetMax = parseNum(params.budget_max);
      const bedroomsMin = parseNum(params.bedrooms_min);
      const propertyType = parseStr(params.property_type);
      const commuteReqs = parseStr(params.commute_requirements);
      const schoolPrefs = parseStr(params.school_preferences);
      const mustHaves = parseList(params.must_haves);
      const lifestylePrefs = parseList(params.lifestyle_preferences);
      const dealBreakers = parseList(params.deal_breakers);

      // Build a natural-language brief from the collected data
      const parts: string[] = [];
      if (locations.length > 0) parts.push(`in ${locations.join(' or ')}`);
      if (bedroomsMin) parts.push(`${bedroomsMin} bedrooms`);
      if (propertyType) parts.push(propertyType);
      if (budgetMax) parts.push(`budget up to £${budgetMax.toLocaleString()}`);
      if (budgetMin) parts.push(`minimum £${budgetMin.toLocaleString()}`);
      if (commuteReqs) parts.push(`commute: ${commuteReqs}`);
      if (schoolPrefs) parts.push(`schools: ${schoolPrefs}`);
      if (mustHaves.length > 0) parts.push(`must have: ${mustHaves.join(', ')}`);
      if (lifestylePrefs.length > 0) parts.push(`lifestyle: ${lifestylePrefs.join(', ')}`);
      if (dealBreakers.length > 0) parts.push(`avoid: ${dealBreakers.join(', ')}`);

      const briefText = `Looking for a property ${parts.join(', ')}`;

      // Step 1: Create session via /api/brief
      const briefRes = await fetch('/api/brief', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ brief: briefText }),
      });

      if (!briefRes.ok) throw new Error('Failed to create search session');
      const briefData = await briefRes.json();
      const sessionId = briefData.session_id;

      // Step 2: Start the search pipeline with the structured data
      const updatedBrief = {
        budget_min: budgetMin,
        budget_max: budgetMax,
        bedrooms_min: bedroomsMin,
        bedrooms_max: null,
        property_type: propertyType,
        locations,
        commute_requirements: commuteReqs,
        school_preferences: schoolPrefs,
        must_haves: mustHaves,
        lifestyle_preferences: lifestylePrefs,
        deal_breakers: dealBreakers,
        additional_notes: null,
      };

      const searchRes = await fetch(`/api/search/${sessionId}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updated_brief: updatedBrief }),
      });

      if (!searchRes.ok) throw new Error('Failed to start search');

      // End the voice call immediately so it doesn't keep listening
      try { await conversation.endSession(); } catch { /* ignore */ }

      onComplete(sessionId);
      return 'Search started successfully.';
    } catch {
      setError('Failed to start search. Please try again.');
      return 'There was an error starting the search. Please apologize and ask the user to try again.';
    } finally {
      setIsSubmitting(false);
    }
  }, [onComplete]);

  const conversation = useConversation({
    onMessage: (message: { source?: string; message?: string }) => {
      if (message.source === 'ai' && message.message) {
        setTranscript((prev) => [...prev, { role: 'agent', text: message.message! }]);
      } else if (message.source === 'user' && message.message) {
        setTranscript((prev) => [...prev, { role: 'user', text: message.message! }]);
      }
    },
    onError: (err: unknown) => {
      console.error('Voice agent error:', err);
      setError('Connection error. Please try again.');
    },
    onDisconnect: () => {
      // Conversation ended
    },
  });

  const { status, isSpeaking } = conversation;

  // Auto-scroll transcript
  useEffect(() => {
    if (transcriptRef.current) {
      transcriptRef.current.scrollTop = transcriptRef.current.scrollHeight;
    }
  }, [transcript]);

  // Start session on mount
  useEffect(() => {
    if (hasStarted.current) return;
    hasStarted.current = true;

    const startVoiceSession = async () => {
      try {
        // Request microphone permission
        await navigator.mediaDevices.getUserMedia({ audio: true });

        // Each persona has its own ElevenLabs agent with voice pre-configured
        const agentIdMap: Record<string, string | undefined> = {
          NEXT_PUBLIC_ELEVENLABS_AGENT_ID_SOPHIE: process.env.NEXT_PUBLIC_ELEVENLABS_AGENT_ID_SOPHIE,
          NEXT_PUBLIC_ELEVENLABS_AGENT_ID_JAMES: process.env.NEXT_PUBLIC_ELEVENLABS_AGENT_ID_JAMES,
        };
        const agentId = agentIdMap[persona.agentIdEnvKey];
        if (!agentId) {
          setError(`Voice agent not configured. Please set ${persona.agentIdEnvKey} in .env.local.`);
          return;
        }

        await conversation.startSession({
          agentId,
          connectionType: 'webrtc',
          clientTools: {
            submitPropertyBrief: handleSubmitBrief,
          },
        });
      } catch (err) {
        console.error('Failed to start voice session:', err);
        setError('Could not start voice session. Please check microphone permissions and try again.');
      }
    };

    startVoiceSession();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEndCall = async () => {
    try {
      await conversation.endSession();
    } catch {
      // Ignore end session errors
    }
    onBack();
  };

  const isConnecting = status === 'connecting';
  const isConnected = status === 'connected';

  return (
    <div className="flex w-full max-w-sm flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-100 px-5 py-4">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-blue-600">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-400">Smart Property Search</p>
            <p className="text-sm font-semibold text-slate-800 leading-tight">Voice Agent</p>
          </div>
        </div>
        <button
          onClick={handleEndCall}
          className="text-xs text-slate-400 hover:text-slate-600 transition"
        >
          Back
        </button>
      </div>

      {/* Agent avatar & status */}
      <div className="flex flex-col items-center py-6 px-5">
        {/* Avatar with pulse ring */}
        <div className="relative mb-3">
          <div className={`flex h-20 w-20 items-center justify-center rounded-full text-white text-2xl font-bold ${
            persona.gender === 'female' ? 'bg-purple-500' : 'bg-sky-500'
          }`}>
            {persona.name[0]}
          </div>
          {/* Speaking pulse */}
          {isSpeaking && (
            <>
              <div className={`absolute inset-0 rounded-full animate-ping opacity-20 ${
                persona.gender === 'female' ? 'bg-purple-400' : 'bg-sky-400'
              }`} />
              <div className={`absolute -inset-1 rounded-full animate-pulse opacity-30 ${
                persona.gender === 'female' ? 'bg-purple-300' : 'bg-sky-300'
              }`} />
            </>
          )}
          {/* Connecting spinner */}
          {isConnecting && (
            <div className="absolute -inset-1 rounded-full border-2 border-transparent border-t-blue-500 animate-spin" />
          )}
        </div>

        <p className="text-base font-semibold text-slate-800">{persona.name}</p>
        <p className="text-xs text-slate-500">
          {isConnecting && 'Connecting...'}
          {isConnected && isSpeaking && 'Speaking...'}
          {isConnected && !isSpeaking && 'Listening...'}
          {!isConnecting && !isConnected && 'Disconnected'}
        </p>

        {/* Audio visualizer bars */}
        {isConnected && (
          <div className="flex items-end gap-0.5 mt-3 h-6">
            {[0, 1, 2, 3, 4, 5, 6].map((i) => (
              <div
                key={i}
                className={`w-1 rounded-full transition-all duration-150 ${
                  isSpeaking
                    ? persona.gender === 'female' ? 'bg-purple-400' : 'bg-sky-400'
                    : 'bg-slate-200'
                }`}
                style={{
                  height: isSpeaking
                    ? `${Math.max(4, Math.random() * 24)}px`
                    : '4px',
                  animationDelay: `${i * 75}ms`,
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Transcript */}
      {transcript.length > 0 && (
        <div
          ref={transcriptRef}
          className="mx-5 mb-4 max-h-48 overflow-y-auto rounded-xl border border-slate-100 bg-slate-50 px-4 py-3 scroll-smooth"
        >
          <div className="flex flex-col gap-2.5">
            {transcript.map((entry, i) => (
              <div key={i} className={`flex ${entry.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-lg px-3 py-1.5 text-xs leading-relaxed ${
                  entry.role === 'user'
                    ? 'bg-blue-600 text-white'
                    : 'bg-white text-slate-700 border border-slate-200'
                }`}>
                  {entry.text}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <p className="mx-5 mb-3 text-xs text-red-500 text-center">{error}</p>
      )}

      {/* Submitting state */}
      {isSubmitting && (
        <div className="flex items-center justify-center gap-2 mx-5 mb-3 text-sm text-blue-600">
          <span className="h-4 w-4 animate-spin rounded-full border-2 border-blue-200 border-t-blue-600" />
          Starting your property search...
        </div>
      )}

      {/* End call button */}
      <div className="border-t border-slate-100 px-5 py-4 flex justify-center">
        <button
          onClick={handleEndCall}
          disabled={isSubmitting}
          className="flex items-center gap-2 rounded-full bg-red-500 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-red-200 transition hover:bg-red-600 active:scale-[0.98] disabled:opacity-40"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M16 8l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M5 3a2 2 0 00-2 2v1c0 8.284 6.716 15 15 15h1a2 2 0 002-2v-3.28a1 1 0 00-.684-.948l-4.493-1.498a1 1 0 00-1.21.502l-1.13 2.257a11.042 11.042 0 01-5.516-5.517l2.257-1.128a1 1 0 00.502-1.21L9.228 3.683A1 1 0 008.279 3H5z" />
          </svg>
          End Call
        </button>
      </div>
    </div>
  );
}
